import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, User } from 'lucide-react';
import { DataTable } from '../../glitch-ui/data-table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { organizationQueryOptions, useResendInvitationMutation } from '@/lib/query-options';
import { removeUserFromOrganization, cancelOrganizationInvite } from '@/lib/apis';
import { toast } from '@/hooks/useToast';
import { MembershipRole, MembershipStatus } from '@/lib/types';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';
import { useUserProfile } from '@/contexts/user.context';
import { ConformationModal } from '../../glitch-ui/confirmation-modal';
import { usePostHog } from 'posthog-js/react';

type Member = {
  id: string;
  name: string;
  email: string;
  picture: string | null;
  access: MembershipRole;
  dateAdded: string;
  status: MembershipStatus;
};

interface MembersTableProps {
  onInviteClick?: () => void;
  organizationId?: number; // Optional: if provided, fetch this org's members instead of current org
}

export const MembersTable = ({ onInviteClick, organizationId }: MembersTableProps) => {
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    email: string;
    name: string;
    isPending: boolean;
    userId?: string;
  }>({ isOpen: false, email: '', name: '', isPending: false, userId: undefined });
  const [isDeleting, setIsDeleting] = useState(false);
  
  const resendInvitationMutation = useResendInvitationMutation();

  const handleCancelInvite = async () => {
    if (!organizationData || !deleteModal.email) return;
    
    setIsDeleting(true);
    try {
      await cancelOrganizationInvite(organizationData.id, deleteModal.email);
      posthog?.capture('org_invite_cancelled');

      // Force refetch both the organization list and specific organization data
      await queryClient.refetchQueries({
        queryKey: ['organizations'] // Force refetch organizations list (for updated counts)
      });
      await queryClient.refetchQueries({
        queryKey: ['organizations', organizationData.id] // Force refetch specific org details
      });

      setDeleteModal({ isOpen: false, email: '', name: '', isPending: false });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to cancel invitation',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResendInvite = async (email: string) => {
    if (!organizationData) return;
    
    resendInvitationMutation.mutate(
      { email, organizationId: organizationData.id },
      {
        onSuccess: () => {
          toast({
            title: 'Success',
            description: 'Invitation resent successfully!',
          });
        },
        onError: (error) => {
          let errorMessage = 'Failed to resend invitation';
          
          if (error.message.includes('Please wait')) {
            errorMessage = error.message;
          } else if (error.message.includes('No pending invitation')) {
            errorMessage = 'No pending invitation found for this email address.';
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          toast({
            title: 'Failed to resend invitation',
            description: errorMessage,
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleRemoveMember = async () => {
    if (!organizationData || !deleteModal.userId) return;
    
    setIsDeleting(true);
    try {
      const result = await removeUserFromOrganization(organizationData.id, parseInt(deleteModal.userId));
      if (result?.msg) {
        toast({
          title: 'Unable to remove member',
          description: result.msg,
          variant: 'destructive',
        });
      } else {
        posthog?.capture('team_member_removed');
        toast({
          title: 'Member removed',
          description: 'The member has been successfully removed from the organization.',
        });

        // Force refetch both the organization list and specific organization data
        await queryClient.refetchQueries({
          queryKey: ['organizations'] // Force refetch organizations list (for updated counts)
        });
        await queryClient.refetchQueries({
          queryKey: ['organizations', organizationData.id] // Force refetch specific org details
        });
      }
      
      setDeleteModal({ isOpen: false, email: '', name: '', isPending: false, userId: undefined });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove member. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const organizationData = useAtomValue(organizationDataAtom);
  const { user: currentUser } = useUserProfile();

  // Use provided ID or fall back to current organization's ID
  const effectiveOrgId = organizationId || organizationData?.id;

  // Check if current user is an owner of the organization
  const isCurrentUserOwner = organizationData?.membership?.role?.toLowerCase() === 'owner';

  // Use organization ID for fetching organization users
  const {
    data: organization,
    error: orgError,
  } = useQuery({
    ...organizationQueryOptions(effectiveOrgId!),
    enabled: !!effectiveOrgId,
  });

  const members: Member[] =
    organization?.users?.map((user) => {
      return {
        id: user.id?.toString() || '', // Will be empty string for pending invitations
        name: user.name,
        email: user.email,
        picture: user.picture ?? null,
        access: user.membership.role,
        dateAdded: new Date(user.date_added).toLocaleDateString('en-GB'),
        status: user.membership.status,
      };
    }).sort((a, b) => {
      // Define role hierarchy: Owner = 0, Admin = 1, Member = 2
      const roleOrder: { [key: string]: number } = {
        owner: 0,
        admin: 1,
        member: 2
      };
      
      const aOrder = roleOrder[a.access.toLowerCase()] ?? 3;
      const bOrder = roleOrder[b.access.toLowerCase()] ?? 3;
      
      return aOrder - bOrder;
    }) || [];

  const columns: ColumnDef<Member>[] = [
    {
      accessorKey: 'name',
      header: 'Members',
      cell: ({ row }) => {
        const name = row.getValue('name') as string;
        const picture = row.original.picture;
        
        return (
          <div className='flex items-center space-x-3'>
            <div className='flex-shrink-0'>
              {picture ? (
                <img 
                  className='h-8 w-8 rounded-full' 
                  src={picture} 
                  alt={name}
                  onError={(e) => {
                    // Fallback to placeholder if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div className={`h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center ${picture ? 'hidden' : ''}`}>
                <User className='h-4 w-4 text-gray-500' />
              </div>
            </div>
            <div className='font-medium'>{name}</div>
          </div>
        );
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => <div>{row.getValue('email')}</div>,
    },
    {
      accessorKey: 'access',
      header: 'Access',
      cell: ({ row }) => {
        const access = row.getValue('access') as string;
        // Capitalize first letter
        const displayAccess = access ? access.charAt(0).toUpperCase() + access.slice(1).toLowerCase() : '';
        return <div>{displayAccess}</div>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as string;
        const statusLower = status?.toLowerCase();
        
        let displayText = '';
        let className = 'text-gray-900';
        
        if (statusLower === 'active') {
          displayText = 'Active';
          className = 'text-gray-900';
        } else if (statusLower === 'pending') {
          // Check if invitation is expired (older than 7 days)
          const user = organization?.users?.find(u => u.email === row.getValue('email'));
          if (user) {
            const inviteDate = new Date(user.date_added);
            const now = new Date();
            const daysSinceInvite = Math.floor((now.getTime() - inviteDate.getTime()) / (1000 * 60 * 60 * 24));
            
            if (daysSinceInvite > 7) {
              displayText = 'Expired';
              className = 'text-red-600';
            } else {
              displayText = 'Pending';
              className = 'text-amber-600';
            }
          } else {
            displayText = 'Pending';
            className = 'text-amber-600';
          }
        }
        
        return (
          <div className={className}>
            {displayText}
          </div>
        );
      },
    },
    {
      accessorKey: 'dateAdded',
      header: 'Date added',
      cell: ({ row }) => <div>{row.getValue('dateAdded')}</div>,
    },
    {
      id: 'actions',
      header: () => (
        <div className="flex justify-end">
          {isCurrentUserOwner && onInviteClick && (
            <Button onClick={onInviteClick} variant='outline' size='sm' className="shadow-none">+ Invite</Button>
          )}
        </div>
      ),
      cell: ({ row }) => {
        const userRole = row.original.access;
        const userEmail = row.original.email;
        const userName = row.original.name;
        const userStatus = row.original.status;
        const isOwner = userRole === 'owner';
        const isCurrentUser = userEmail === currentUser.email;
        const statusLower = userStatus?.toLowerCase();
        // Check if invitation is expired (older than 7 days for pending invitations)
        let isExpired = false;
        if (statusLower === 'pending') {
          const user = organization?.users?.find(u => u.email === userEmail);
          if (user) {
            const inviteDate = new Date(user.date_added);
            const now = new Date();
            const daysSinceInvite = Math.floor((now.getTime() - inviteDate.getTime()) / (1000 * 60 * 60 * 24));
            isExpired = daysSinceInvite > 7;
          }
        }
        const isPending = statusLower === 'pending' || isExpired;
        
        // Hide actions menu if:
        // 1. Current user is not an owner of the organization
        // 2. Target user is an owner (owners cannot be deleted)
        // 3. Target user is the current user (cannot delete themselves)
        if (!isCurrentUserOwner || isOwner || isCurrentUser) {
          return <div className="text-right"></div>;
        }
        
        return (
          <div className="text-right">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' className='h-8 w-8 p-0'>
                  <span className='sr-only'>Open menu</span>
                  <MoreHorizontal className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                {isPending ? (
                  <>
                    <DropdownMenuItem 
                      onClick={() => handleResendInvite(userEmail)}
                      disabled={resendInvitationMutation.isPending}
                      className='cursor-pointer'
                    >
                      {resendInvitationMutation.isPending ? 'Resending...' : 'Resend Invitation'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setDeleteModal({
                        isOpen: true,
                        email: userEmail,
                        name: userName,
                        isPending: true
                      })} 
                      className='text-destructive cursor-pointer'
                    >
                      Cancel Invitation
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem 
                    onClick={() => {
                      // Safety check: don't try to remove users without valid ID
                      if (!row.original.id || row.original.id === '') {
                        toast({
                          title: 'Error',
                          description: 'Cannot remove user: Invalid user ID',
                          variant: 'destructive',
                        });
                        return;
                      }
                      
                      setDeleteModal({
                        isOpen: true,
                        email: userEmail,
                        name: userName,
                        isPending: false,
                        userId: row.original.id
                      });
                    }} 
                    className='text-destructive cursor-pointer'
                  >
                    Remove Member
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];


  return (
    <div>
      {/* Error handling inside return statement */}
      {orgError ? (
        <div>Error loading organization. Please try again.</div>
      ) : (
        <>
          {members.length === 0 ? (
            <div />
          ) : (
            <div className="[&>div]:border-0 [&>div]:shadow-none">
              <DataTable data={members} columns={columns} itemsName='member' pageSize={999} />
            </div>
          )}
        </>
      )}
      
      {/* Confirmation Modal for canceling invitations and removing members */}
      <ConformationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, email: '', name: '', isPending: false, userId: undefined })}
        onConfirm={deleteModal.isPending ? handleCancelInvite : handleRemoveMember}
        title={deleteModal.isPending ? 'Cancel Invitation' : 'Remove Member'}
        description={
          deleteModal.isPending 
            ? `Are you sure you want to cancel the invitation for ${deleteModal.email}? They will not be able to join using their invitation link.`
            : `Are you sure you want to remove ${deleteModal.name} from the organization?`
        }
        confirmText={deleteModal.isPending ? 'Cancel Invitation' : 'Remove'}
        cancelText='Keep'
        isLoading={isDeleting}
      />
    </div>
  );
};
